import { StateError } from '../errors';
import {
  edgeId,
  hexCornerVertex,
  hexCornerVertices,
  hexId,
  vertexHexes,
  vertexNeighbors,
  type CornerName,
  type EdgeId,
  type HexCoord,
  type HexId,
  type VertexId,
} from './axial';
import type { BanditStartJson, HarborDir, MapCellRef, MapCustomJson, MapFixedPieceJson, MapFogJson, MapOverridesJson, MapSeaConstraints, MapSeaRules, MapWaterJson } from './mapSchema';

// The derived board (plan §3.4): built once from a ResolvedMap at game creation and on every
// client from config — never stored in GameState. Vertices/edges are DERIVED from hex
// adjacency (brief §6.3): a vertex exists iff it touches ≥1 CELL; an edge exists iff it
// borders ≥1 cell (coastal edges legal, outline §7). Islands/holes fall out for free.
//
// H20-S1: a cell is land, water or fog. A classic map has only land, so its graph is exactly
// what it always was; a sea map lists its water and fog explicitly, and the water|water edges
// ships sail on exist because a water cell is a cell. `hexes` stays LAND ONLY — so every
// existing consumer (harvest, bandit targets, steal adjacency, the renderer's tiles) stays
// land-only by construction, and nothing hands a water cell a tray or a harvest pass.

/** A fully concrete map: post-generation, no 'random'/'auto' left (plan §17.2). */
export interface ResolvedMap {
  name: string;
  minSeats: number;
  maxSeats: number;
  hexes: Array<{ q: number; r: number; tile: string; disc: number | null }>;
  harbors: Array<{ q: number; r: number; dir: HarborDir; kind: string }>;
  fixedPieces: MapFixedPieceJson[];
  overrides: MapOverridesJson;
  /** H20-S1: the sea block, post-resolution. ABSENT on every classic map (byte-identical). */
  sea?: ResolvedSea;
  /** The map editor's block, copied through (it holds no draws). ABSENT on every shipped map. */
  custom?: MapCustomJson;
}

/**
 * The sea block as dealt: fixed water plus any water the deal placed, the fog's CELLS and its
 * pool as a public MULTISET (never its order — the ResolvedMap rides every client's Welcome),
 * and the scenario's rule numbers and anchors.
 */
export interface ResolvedSea {
  waters: MapWaterJson[];
  frameEdges: Array<{ q: number; r: number; dir: HarborDir }>;
  fog: MapFogJson | null;
  setupIslands: MapCellRef[] | null;
  landfallVp: number;
  banditStart: BanditStartJson;
  pirateStart: MapCellRef | null;
  rules: MapSeaRules;
  constraints: MapSeaConstraints;
}

export interface BoardHex {
  id: HexId;
  coord: HexCoord;
  tile: string;
  disc: number | null;
}

/** H20-S1: an open-water cell; `frame` = the outer frame ring. */
export interface BoardWater {
  id: HexId;
  coord: HexCoord;
  frame: boolean;
}

/** H20-S1: an unexplored cell — its contents are decided in play (the live board). */
export interface BoardFog {
  id: HexId;
  coord: HexCoord;
}

export interface BoardVertex {
  id: VertexId;
  /** Adjacent LAND hexes (0–3; ≥ 1 on every classic map). */
  hexes: HexId[];
  /** H20-S1: adjacent WATER cells (always empty on a classic map). */
  waters: HexId[];
  /** H20-S1: adjacent unexplored cells. */
  fog: HexId[];
  /** Adjacent vertices that exist on this board (2–3). */
  neighbors: VertexId[];
  /** Incident edges that exist on this board. */
  edges: EdgeId[];
  /** Harbor kind granted by a building here ('3:1' | resource id | null). */
  harbor: string | null;
}

export interface BoardEdge {
  id: EdgeId;
  vertices: [VertexId, VertexId];
  /** Adjacent LAND hexes (0–2); on a classic map 1 = coastal edge. */
  hexes: HexId[];
  /** H20-S1: adjacent WATER cells. */
  waters: HexId[];
  /** H20-S1: adjacent unexplored cells. */
  fog: HexId[];
}

export interface BoardGraph {
  /** LAND only (H20-S1) — see the header. */
  hexes: ReadonlyMap<HexId, BoardHex>;
  /** H20-S1: open water (empty on a classic map). */
  waters: ReadonlyMap<HexId, BoardWater>;
  /** H20-S1: unexplored cells (empty on a classic map). */
  fog: ReadonlyMap<HexId, BoardFog>;
  vertices: ReadonlyMap<VertexId, BoardVertex>;
  edges: ReadonlyMap<EdgeId, BoardEdge>;
  /** The harbor edge each map harbor resolved to (both its endpoints carry the kind). */
  harborEdges: ReadonlyArray<{ edge: EdgeId; kind: string }>;
  /** H20-S1: the frame|frame edges this map allows ships on (the frame is closed elsewhere). */
  frameEdges: ReadonlySet<EdgeId>;
}

/** HarborDir → the corner-ring pair naming that edge (P0 EDGE_FACES verification):
 *  the edge between consecutive ring corners N–NE faces the NE neighbor, NE–SE faces E, … */
const DIR_TO_CORNERS: Readonly<Record<HarborDir, [CornerName, CornerName]>> = Object.freeze({
  NE: ['N', 'NE'],
  E: ['NE', 'SE'],
  SE: ['SE', 'S'],
  SW: ['S', 'SW'],
  W: ['SW', 'NW'],
  NW: ['NW', 'N'],
});

/** Resolve a hex + facing direction to the edge id on that side (plan §6.7 anchors). */
export function harborEdgeOf(q: number, r: number, dir: HarborDir): EdgeId {
  const [a, b] = DIR_TO_CORNERS[dir];
  return edgeId(hexCornerVertex(q, r, a), hexCornerVertex(q, r, b));
}

/** Resolve a fixed-piece vertex ref to its canonical id. */
export function fixedVertexOf(ref: { q: number; r: number; c: CornerName }): VertexId {
  // refs address the vertex CLASS directly (N/S canonical form), not a hex corner name
  if (ref.c !== 'N' && ref.c !== 'S') throw new StateError(`fixed piece vertex class must be N|S, got ${String(ref.c)}`);
  return `v${ref.q},${ref.r},${ref.c}`;
}

export function buildBoard(map: ResolvedMap): BoardGraph {
  const hexes = new Map<HexId, BoardHex>();
  for (const h of map.hexes) {
    const id = hexId(h.q, h.r);
    if (hexes.has(id)) throw new StateError(`buildBoard: duplicate hex ${id}`);
    hexes.set(id, { id, coord: { q: h.q, r: h.r }, tile: h.tile, disc: h.disc });
  }
  const waters = new Map<HexId, BoardWater>();
  for (const w of map.sea?.waters ?? []) {
    const id = hexId(w.q, w.r);
    if (hexes.has(id) || waters.has(id)) throw new StateError(`buildBoard: duplicate cell ${id}`);
    waters.set(id, { id, coord: { q: w.q, r: w.r }, frame: w.frame === true });
  }
  const fog = new Map<HexId, BoardFog>();
  for (const f of map.sea?.fog?.cells ?? []) {
    const id = hexId(f.q, f.r);
    if (hexes.has(id) || waters.has(id) || fog.has(id)) throw new StateError(`buildBoard: duplicate cell ${id}`);
    fog.set(id, { id, coord: { q: f.q, r: f.r } });
  }
  const isLand = (c: HexCoord): boolean => hexes.has(hexId(c.q, c.r));
  const domainsAt = (v: VertexId): { hexes: HexId[]; waters: HexId[]; fog: HexId[] } => {
    const around = vertexHexes(v).map((c) => hexId(c.q, c.r));
    return { hexes: around.filter((id) => hexes.has(id)), waters: around.filter((id) => waters.has(id)), fog: around.filter((id) => fog.has(id)) };
  };

  // vertices: union of every cell's corners — land first, in map order, so a classic map's
  // insertion order is exactly what it always was (legalActions enumerates in Map order)
  const cells: HexCoord[] = [...hexes.values()].map((h) => h.coord);
  for (const w of waters.values()) cells.push(w.coord);
  for (const f of fog.values()) cells.push(f.coord);
  const vertices = new Map<VertexId, BoardVertex>();
  for (const c of cells) {
    for (const v of hexCornerVertices(c.q, c.r)) {
      if (!vertices.has(v)) vertices.set(v, { id: v, ...domainsAt(v), neighbors: [], edges: [], harbor: null });
    }
  }

  // edges: union of every cell's corner-ring edges; an edge's cells = those adjacent to BOTH ends
  const edges = new Map<EdgeId, BoardEdge>();
  for (const c of cells) {
    const corners = hexCornerVertices(c.q, c.r);
    for (let i = 0; i < 6; i++) {
      const a = corners[i]!;
      const b = corners[(i + 1) % 6]!;
      const id = edgeId(a, b);
      if (!edges.has(id)) edges.set(id, edgeFrom(id, a, b, isLand, (c2) => waters.has(hexId(c2.q, c2.r)), (c2) => fog.has(hexId(c2.q, c2.r))));
    }
  }

  // wire vertex adjacency restricted to the board
  for (const v of vertices.values()) {
    for (const w of vertexNeighbors(v.id)) {
      if (vertices.has(w)) {
        v.neighbors.push(w);
        const e = edgeId(v.id, w);
        if (edges.has(e)) v.edges.push(e);
      }
    }
  }

  // harbors: resolve each map harbor to its edge, grant both endpoints
  const harborEdges: Array<{ edge: EdgeId; kind: string }> = [];
  for (const h of map.harbors) {
    if (!isLand({ q: h.q, r: h.r })) throw new StateError(`buildBoard: harbor anchor h${h.q},${h.r} is not a land hex`);
    const e = harborEdgeOf(h.q, h.r, h.dir);
    const edge = edges.get(e);
    if (!edge) throw new StateError(`buildBoard: harbor edge ${e} does not exist`);
    harborEdges.push({ edge: e, kind: h.kind });
    for (const vid of edge.vertices) {
      const v = vertices.get(vid);
      if (!v) throw new StateError(`buildBoard: harbor vertex ${vid} missing`);
      if (v.harbor !== null && v.harbor !== h.kind) {
        throw new StateError(`buildBoard: vertex ${vid} granted two different harbor kinds`);
      }
      v.harbor = h.kind;
    }
  }

  const frameEdges = new Set<EdgeId>();
  for (const f of map.sea?.frameEdges ?? []) frameEdges.add(harborEdgeOf(f.q, f.r, f.dir));

  // structural sanity the whole engine leans on (violations here are map-resolution bugs)
  for (const v of vertices.values()) {
    const n = v.hexes.length + v.waters.length + v.fog.length;
    if (n < 1 || n > 3) throw new StateError(`buildBoard: vertex ${v.id} touches ${n} cells`);
  }
  for (const e of edges.values()) {
    const n = e.hexes.length + e.waters.length + e.fog.length;
    if (n < 1 || n > 2) throw new StateError(`buildBoard: edge ${e.id} borders ${n} cells`);
  }
  return { hexes, waters, fog, vertices, edges, harborEdges, frameEdges };
}

/** An edge's cells = those adjacent to BOTH endpoints, in `a`'s corner order (the order a
 *  classic board has always had — its `hexes` lists are pinned by T-SEA-001). */
function edgeFrom(id: EdgeId, a: VertexId, b: VertexId, isLand: (c: HexCoord) => boolean, isWater: (c: HexCoord) => boolean, isFog: (c: HexCoord) => boolean): BoardEdge {
  const [lo, hi] = id.slice(1).split('|') as [VertexId, VertexId];
  const bKeys = new Set(vertexHexes(b).map((c) => `${c.q},${c.r}`));
  const shared = vertexHexes(a).filter((c) => bKeys.has(`${c.q},${c.r}`));
  return {
    id,
    vertices: [lo, hi],
    hexes: shared.filter(isLand).map((c) => hexId(c.q, c.r)),
    waters: shared.filter(isWater).map((c) => hexId(c.q, c.r)),
    fog: shared.filter(isFog).map((c) => hexId(c.q, c.r)),
  };
}

/**
 * H20-S1: the board with some unexplored cells DECIDED — land (with its disc) or water. The
 * graph's shape never changes (a fog cell's corners and edges already exist); a revealed cell
 * moves out of `fog` into `hexes` or `waters`, and the vertices and edges around it are copied
 * with their domain lists recomputed exactly as `buildBoard` computes them. Everything else is
 * SHARED with `board`. `reveals` must be in a canonical order — the caller's contract: a
 * Record that round-trips through canonical JSON comes back key-sorted (D25), so iterating its
 * insertion order would build a different Map on replay than live.
 */
export function revealCells(board: BoardGraph, reveals: ReadonlyArray<readonly [HexId, { tile: string; disc: number | null } | 'water']>): BoardGraph {
  const hexes = new Map(board.hexes);
  const waters = new Map(board.waters);
  const fog = new Map(board.fog);
  const touched = new Set<HexId>();
  for (const [id, cell] of reveals) {
    const f = fog.get(id);
    if (!f) throw new StateError(`revealCells: ${id} is not an unexplored cell`);
    fog.delete(id);
    touched.add(id);
    if (cell === 'water') waters.set(id, { id, coord: f.coord, frame: false });
    else hexes.set(id, { id, coord: f.coord, tile: cell.tile, disc: cell.disc });
  }
  const isLand = (c: HexCoord): boolean => hexes.has(hexId(c.q, c.r));
  const isWater = (c: HexCoord): boolean => waters.has(hexId(c.q, c.r));
  const isFog = (c: HexCoord): boolean => fog.has(hexId(c.q, c.r));
  const vertices = new Map<VertexId, BoardVertex>();
  for (const [vid, v] of board.vertices) {
    const around = vertexHexes(vid).map((c) => hexId(c.q, c.r));
    if (!around.some((id) => touched.has(id))) {
      vertices.set(vid, v);
      continue;
    }
    vertices.set(vid, {
      ...v,
      hexes: around.filter((id) => hexes.has(id)),
      waters: around.filter((id) => waters.has(id)),
      fog: around.filter((id) => fog.has(id)),
    });
  }
  const edges = new Map<EdgeId, BoardEdge>();
  for (const [eid, e] of board.edges) {
    if (![...e.fog].some((id) => touched.has(id))) {
      edges.set(eid, e);
      continue;
    }
    edges.set(eid, edgeFrom(eid, e.vertices[0], e.vertices[1], isLand, isWater, isFog));
  }
  return { hexes, waters, fog, vertices, edges, harborEdges: board.harborEdges, frameEdges: board.frameEdges };
}